import React, { useState, useEffect, useCallback } from "react";
import {
  FilterBar,
  Field,
  Select,
  SearchInput,
  Spinner,
  EmptyState,
  Dialog,
  FioriButton,
  IconButton,
} from "../components/FioriUI";
import config from "../config";

// ─── Endpoints ────────────────────────────────────────────────────────────────
const CREDIT_API_URL = "/api/credit-management/orders";
const WEBHOOK_URL = `/api/webhook/recipe/${config.creditManagement.webhookRecipeId}`;
const API_TOKEN = config.creditManagement.apiToken;

const COLORS = {
  navy: "#131a33",
  blue: "#2447b0",
  blueDark: "#1d3fa8",
  blueSoft: "#e8eefb",
  blueText: "#1e3a8a",
  ink: "#111827",
  muted: "#6b7280",
  line: "#e5e7eb",
  page: "#f7f8fa",
  green: "#15803d",
  greenBg: "#eaf7ee",
  amber: "#a16207",
  amberBg: "#fdf4e3",
  red: "#b91c1c",
  redBg: "#fdecec",
};

// ─── SOP Data ─────────────────────────────────────────────────────────────────
const SOPS = {
  credit: {
    title: "CM-OPS · Credit Management Scenario Dispatch",
    sub: "Credit Management Exception Standard · v1.0",
    rows: [
      { s: "CM_WITHIN_LIMIT", c: "Exposure ≤ 100% of limit, no adverse signal", d: "AUTO_RELEASE", t: "—" },
      { s: "CM_TOLERANCE_BAND", c: "Exposure 100–110%, clean aging, days-to-pay stable", d: "AUTO_RELEASE", t: "—" },
      { s: "CM_OVER_LIMIT_CLEAN", c: "Exposure 110–150%, no aging deterioration", d: "ESCALATE", t: "T2" },
      { s: "CM_AGING_DETERIORATION", c: "Any balance in 61–90 bucket, or days-to-pay worsened > 25%", d: "ESCALATE", t: "T2" },
      { s: "CM_SEVERE_EXPOSURE", c: "Exposure 150–200%, or 3+ blocking checks", d: "HOLD", t: "T3" },
      { s: "CM_CRITICAL_EXPOSURE", c: "Exposure > 200%, or order > $100,000", d: "HOLD", t: "T4" },
      { s: "CM_BLOCKING", c: "Any 90+ balance, active collections, returned payment in 90 days", d: "HOLD", t: "T3" },
    ],
  },
};

// ─── Shared UI Components ─────────────────────────────────────────────────────
function Pill({ tone, children }) {
  const map = {
    g: { bg: COLORS.greenBg, c: COLORS.green },
    a: { bg: COLORS.amberBg, c: COLORS.amber },
    r: { bg: COLORS.redBg, c: COLORS.red },
    b: { bg: COLORS.blueSoft, c: COLORS.blueText },
  };
  const s = map[tone] || map.b;
  return (
    <span style={{ display: "inline-block", fontSize: 10.5, fontWeight: 600, padding: "4px 9px", borderRadius: 5, whiteSpace: "nowrap", background: s.bg, color: s.c }}>
      {children}
    </span>
  );
}

function SopChip({ label, onClick }) {
  return (
    <span onClick={(e) => { e.stopPropagation(); onClick(); }}
      style={{ display: "inline-flex", alignItems: "center", gap: 5, background: "#eef2ff", border: "1px solid #c7d2fe", color: "#3730a3", borderRadius: 5, padding: "3px 8px", fontSize: 10.5, fontWeight: 600, cursor: "pointer", fontFamily: "ui-monospace,monospace" }}>
      {label}
    </span>
  );
}

function Kpi({ label, value, sub, tone }) {
  const toneColor = tone === "g" ? COLORS.green : tone === "a" ? COLORS.amber : tone === "r" ? COLORS.red : COLORS.ink;
  return (
    <div style={{ background: "#fff", border: `1px solid ${COLORS.line}`, borderRadius: 10, padding: "14px 16px" }}>
      <div style={{ fontSize: 10, color: COLORS.muted, textTransform: "uppercase", letterSpacing: 0.6, fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: 27, fontWeight: 600, marginTop: 5, lineHeight: 1.1, color: toneColor }}>{value}</div>
      <div style={{ fontSize: 11, color: COLORS.muted, marginTop: 2 }}>{sub}</div>
    </div>
  );
}

function Row({ l, r, rColor }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "7px 0", borderBottom: "1px solid #f1f3f5", fontSize: 13 }}>
      <span style={{ color: COLORS.muted }}>{l}</span>
      <span style={{ textAlign: "right", fontWeight: 500, color: rColor || COLORS.ink }}>{r}</span>
    </div>
  );
}

function Card({ title, children }) {
  return (
    <div style={{ background: "#fff", border: `1px solid ${COLORS.line}`, borderRadius: 9, overflow: "hidden", marginBottom: 14 }}>
      <h4 style={{ margin: 0, background: "#3560c9", color: "#fff", fontSize: 10.5, textTransform: "uppercase", letterSpacing: 0.6, padding: "9px 13px", fontWeight: 600 }}>
        {title}
      </h4>
      <div style={{ padding: 13 }}>{children}</div>
    </div>
  );
}

function Step({ n, status, desc, agent, tone }) {
  const dotBg = tone === "p" ? "#d97706" : tone === "s" ? "#9ca3af" : COLORS.blue;
  const boxBg = tone === "p" ? COLORS.amberBg : tone === "s" ? "#f3f4f6" : COLORS.blueSoft;
  const stColor = tone === "p" ? COLORS.amber : tone === "s" ? "#6b7280" : COLORS.blueText;
  return (
    <div style={{ display: "flex", gap: 11, marginBottom: 9 }}>
      <div style={{ minWidth: 23, height: 23, borderRadius: "50%", background: dotBg, color: "#fff", fontSize: 11, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 600, marginTop: 1 }}>
        {n}
      </div>
      <div style={{ background: boxBg, borderRadius: 7, padding: "9px 11px", flex: 1 }}>
        <div style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: 0.5, color: stColor }}>{status}</div>
        <div style={{ fontSize: 12.5, marginTop: 2, lineHeight: 1.5 }}>{desc}</div>
        <div style={{ fontSize: 10.5, color: COLORS.muted, marginTop: 3, fontFamily: "ui-monospace,monospace" }}>{agent}</div>
      </div>
    </div>
  );
}

function KV({ k, v, mono }) {
  return (
    <div>
      <div style={{ fontSize: 10, color: COLORS.muted, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600 }}>{k}</div>
      <div style={{ fontSize: 13.5, marginTop: 2, fontFamily: mono ? "ui-monospace,monospace" : "inherit" }}>{v}</div>
    </div>
  );
}

function SopTable({ rows }) {
  return (
    <table style={{ width: "100%", borderCollapse: "collapse", border: `1px solid ${COLORS.line}`, fontSize: 12 }}>
      <thead>
        <tr>
          {["Scenario", "Condition", "Decision", "Tier"].map(h => (
            <th key={h} style={{ background: "#f8fafc", textAlign: "left", padding: "8px 10px", borderBottom: `1px solid ${COLORS.line}`, fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, color: COLORS.muted }}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.s} style={r.match ? { background: "#fef9e7", fontWeight: 500 } : {}}>
            <td style={{ padding: "7px 10px", borderBottom: `1px solid ${COLORS.line}`, fontFamily: "ui-monospace,monospace", fontSize: 11 }}>{r.s}</td>
            <td style={{ padding: "7px 10px", borderBottom: `1px solid ${COLORS.line}` }}>{r.c}</td>
            <td style={{ padding: "7px 10px", borderBottom: `1px solid ${COLORS.line}`, fontWeight: 600 }}>{r.d}</td>
            <td style={{ padding: "7px 10px", borderBottom: `1px solid ${COLORS.line}` }}>{r.t}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

const btnStyle = {
  padding: "7px 13px", borderRadius: 6, border: `1px solid ${COLORS.line}`,
  background: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer", color: COLORS.ink, transition: "all 0.15s",
};
const btnDisabledStyle = { opacity: 0.5, cursor: "not-allowed" };
const dcloseStyle = { background: "rgba(255,255,255,.15)", border: "none", color: "#fff", fontSize: 22, width: 32, height: 32, borderRadius: 6, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" };

const tdStyle = (idx, total) => ({
  padding: "11px 14px", borderBottom: idx === total - 1 ? "none" : `1px solid ${COLORS.line}`, fontSize: 13, verticalAlign: "top",
});

// ─── Main Component ───────────────────────────────────────────────────────────
export default function CreditManagement() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [selectedOrder, setSelectedOrder] = useState(null);
  const [sidebarLoading, setSidebarLoading] = useState(false);
  const [sidebarError, setSidebarError] = useState(null);
  const [decisionLoading, setDecisionLoading] = useState(false);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [sopKey, setSopKey] = useState(null);
  const [expandedActions, setExpandedActions] = useState({});
  const [filters, setFilters] = useState({ decision: "", branch: "", search: "" });

  // Modal state for confirmations and alerts
  const [modal, setModal] = useState(null); // { type: 'confirm'|'alert', title, message, onConfirm }
  const [partialAmountInput, setPartialAmountInput] = useState('');
  const [notesInput, setNotesInput] = useState('');

  const [branches, setBranches] = useState([]);
  const [stats, setStats] = useState({
    total_orders: 0, auto_released: 0, escalated: 0, on_hold: 0,
    unique_branches: 0, auto_release_rate: 0, avg_time_to_release_minutes: 0, avg_exposure_pct: 0,
  });

  const anyOpen = drawerOpen || sopKey;
  const openSop = () => setSopKey("credit");
  const closeSop = () => setSopKey(null);
  const closeAll = () => { setDrawerOpen(false); setSopKey(null); };

  const toggleActions = (id) => setExpandedActions((p) => ({ ...p, [id]: !p[id] }));

  const renderActionDetails = (actionDetails) => {
    if (!actionDetails || !Array.isArray(actionDetails) || actionDetails.length === 0) {
      return <span style={{ color: COLORS.muted }}>-</span>;
    }
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
        {actionDetails.map((action, idx) => {
          const toneColor = action.tone === "g" ? COLORS.green : action.tone === "a" ? COLORS.amber : action.tone === "r" ? COLORS.red : COLORS.ink;
          return (
            <div key={idx} style={{ color: toneColor, fontSize: 14, lineHeight: 1.4, fontWeight: 500 }}>
              • {action.text || 'No description'}
            </div>
          );
        })}
      </div>
    );
  };

  // Fetch orders
  const fetchOrders = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const queryParams = new URLSearchParams();
      if (filters.decision) queryParams.append('decision', filters.decision);
      if (filters.branch) queryParams.append('branch', filters.branch);
      if (filters.search) queryParams.append('search', filters.search);

      const url = `${CREDIT_API_URL}?${queryParams.toString()}`;
      const response = await fetch(url);
      if (!response.ok) throw new Error("Failed to fetch credit orders");
      const data = await response.json();
      setOrders(data.orders || []);
    } catch (err) {
      console.error("Error fetching credit orders:", err);
      setError(err.message);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  // Fetch filter options and stats on mount
  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        const branchesRes = await fetch(`${CREDIT_API_URL.replace('/orders', '')}/branches`);
        if (branchesRes.ok) {
          const branchesData = await branchesRes.json();
          setBranches(branchesData.branches || []);
        }
        const statsRes = await fetch(`${CREDIT_API_URL.replace('/orders', '')}/stats`);
        if (statsRes.ok) {
          const statsData = await statsRes.json();
          setStats(statsData);
        }
      } catch (err) {
        console.error('Error fetching filter options:', err);
      }
    };
    fetchFilterOptions();
  }, []);

  const handleRefresh = () => fetchOrders();
  const handleClearFilters = () => setFilters({ decision: "", branch: "", search: "" });

  // Fetch order details for sidebar
  const fetchOrderDetails = async (orderId) => {
    setSidebarLoading(true);
    setSidebarError(null);
    try {
      const response = await fetch(`${CREDIT_API_URL}/${orderId}`);
      if (!response.ok) throw new Error("Failed to fetch order details");
      const data = await response.json();
      setSelectedOrder(data);
    } catch (err) {
      console.error("Error fetching order details:", err);
      setSidebarError(err.message);
      setSelectedOrder(null);
    } finally {
      setSidebarLoading(false);
    }
  };

  const handleViewDetails = (orderId) => {
    setDrawerOpen(true);
    fetchOrderDetails(orderId);
  };

  // Handle decision actions
  const handleOrderDecision = async (decision) => {
    if (!selectedOrder) { 
      setModal({
        type: 'alert',
        title: 'No Order Selected',
        message: 'Please select an order before making a decision.',
      });
      return;
    }
    
    const runId = selectedOrder.run_id;
    const orderId = selectedOrder.order_id;
    
    // For partial release, show input modal first
    if (decision === 'PARTIAL_RELEASE') {
      setModal({
        type: 'partial_input',
        title: 'Partial Release',
        decision: decision,
      });
      return;
    }
    
    // For other decisions, show confirmation modal
    const confirmMessages = {
      REJECTED: 'Are you sure you want to reject this order?',
      RELEASED: 'Are you sure you want to release this order in full?',
    };
    
    setModal({
      type: 'confirm',
      title: `Confirm ${decision === 'REJECTED' ? 'Rejection' : 'Release'}`,
      message: confirmMessages[decision],
      onConfirm: () => submitDecision(decision, runId, orderId, null, ''),
    });
  };

  // Submit decision to webhook
  const submitDecision = async (decision, runId, orderId, partialAmount, userNotes) => {
    // Map UI decision to webhook action codes
    const decisionMap = {
      REJECTED: 'REJECT',
      RELEASED: 'RELEASE_FULL',
      PARTIAL_RELEASE: 'PARTIAL_RELEASE'
    };
    
    setDecisionLoading(true);
    setModal(null); // Close any open modal
    
    try {
      // Call Karini webhook - the agent will update credit_workflow_state
      const webhookPayload = {
        files: [],
        input_message: "HITL_DECISION",
        metadata: {
          action: "HITL_DECISION",
          run_id: runId,
          order_id: orderId,
          decision: decisionMap[decision],
          partial_amount: partialAmount,
          notes: userNotes || `${decision} via Credit Management portal`
        }
      };
      
      console.log('[Credit HITL] Triggering webhook:', webhookPayload);
      
      const webhookRes = await fetch(WEBHOOK_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-token': API_TOKEN // Backend proxy forwards this to Karini
        },
        body: JSON.stringify(webhookPayload)
      });
      
      if (!webhookRes.ok) {
        const errorText = await webhookRes.text();
        console.error('[Credit HITL] Webhook failed:', webhookRes.status, errorText);
        throw new Error(`Webhook call failed: ${webhookRes.status}`);
      }
      
      const webhookResult = await webhookRes.json();
      console.log('[Credit HITL] Webhook response:', webhookResult);
      
      // Success - close drawer and refresh without showing modal
      fetchOrders(); // Refresh the table
      setDrawerOpen(false);
      setSelectedOrder(null);
      
    } catch (err) {
      console.error('[Credit HITL] Error:', err);
      setModal({
        type: 'alert',
        title: 'Submission Failed',
        message: `Failed to submit decision: ${err.message}`,
      });
    } finally {
      setDecisionLoading(false);
      setPartialAmountInput('');
      setNotesInput('');
    }
  };

  // Open source document PDF
  const handleOpenSourceDocument = async (orderId) => {
    try {
      const response = await fetch(`${CREDIT_API_URL}/${orderId}/presigned-url`);
      if (!response.ok) throw new Error('Failed to get document URL');
      const data = await response.json();
      if (data.presigned_url) { 
        window.open(data.presigned_url, '_blank'); 
      } else { 
        setModal({
          type: 'alert',
          title: 'Document Not Available',
          message: 'No document URL available for this order.',
        });
      }
    } catch (err) {
      console.error('Error opening source document:', err);
      setModal({
        type: 'alert',
        title: 'Error',
        message: `Failed to open document: ${err.message}`,
      });
    }
  };

  // ─── Render ───────────────────────────────────────────────────────────────────
  return (
    <div style={{ fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif", background: COLORS.page, color: COLORS.ink, fontSize: 14, minHeight: "100vh" }}>
      <div style={{ padding: "20px 22px 60px", maxWidth: 1500, margin: "0 auto" }}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "flex-start", marginBottom: 16 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 600 }}>Credit Management</h2>
            <p style={{ margin: "2px 0 0", color: COLORS.muted, fontSize: 13 }}>Credit risk assessment, limit checks & approval workflow</p>
          </div>
          <div style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center" }}>
            <button
              type="button"
              onClick={() => window.open("/sop-editor?doc=credit-management", "_blank")}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium border border-fiori-border-dark rounded-fiori text-fiori-brand bg-transparent hover:bg-fiori-brand/5 transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256" fill="currentColor" className="w-7 h-7">
                <g transform="translate(1.407 1.407) scale(2.81 2.81)">
                  <path d="M24.731 57.127v-4.129c0-.444-.361-.806-.806-.806H20.05v5.74h3.876c.444 0 .805-.362.805-.805z"/>
                  <path d="M39.841 63.508V53.286c0-.604-.491-1.094-1.094-1.094H35.16v12.41h3.587c.604 0 1.094-.492 1.094-1.094z"/>
                  <path d="M81.979 17.233L66.215 1.469A5.013 5.013 0 0062.67 0H20.59c-2.764 0-5.013 2.249-5.013 5.013v36.168h-4.28c-2.617 0-4.745 2.084-4.745 4.646v25.139c0 2.563 2.128 4.646 4.745 4.646h4.28v9.374c0 2.764 2.249 5.013 5.013 5.013h57.844c2.764 0 5.013-2.249 5.013-5.013v-64.21a5.013 5.013 0 00-1.468-3.543zM18.05 68.603c-1.104 0-2-.896-2-2v-6.67v-9.74c0-1.104.896-2 2-2h5.876c2.65 0 4.806 2.156 4.806 4.806v4.129c0 2.649-2.156 4.806-4.806 4.806H20.05v4.67c0 1.103-.896 1.999-2 1.999zM79.447 84.987c0 .559-.454 1.013-1.013 1.013H20.59c-.559 0-1.013-.454-1.013-1.013v-9.374h44.425c2.616 0 4.745-2.084 4.745-4.646V45.828c0-2.562-2.129-4.646-4.745-4.646H19.578V5.013C19.578 4.454 20.032 4 20.59 4h41.328v12.784c0 2.617 2.129 4.745 4.745 4.745h12.784v63.458zM31.16 66.603v-16.41c0-1.104.896-2 2-2h5.587c2.809 0 5.094 2.285 5.094 5.094v10.222c0 2.81-2.285 5.095-5.094 5.095H33.16c-1.105 0-2-.896-2-2zM54.255 56.397c1.104 0 2 .896 2 2s-.896 2-2 2h-3.686v6.205c0 1.104-.896 2-2 2s-2-.896-2-2v-16.41c0-1.104.896-2 2-2h8.682c1.104 0 2 .896 2 2s-.896 2-2 2h-6.682v4.205h3.686z"/>
                </g>
              </svg>
              SOP
            </button>
            <button onClick={handleRefresh} style={{ ...btnStyle, background: COLORS.blue, borderColor: COLORS.blue, color: "#fff" }} disabled={loading}>
              {loading ? "Refreshing..." : "Refresh"}
            </button>
          </div>
        </div>

        {/* KPIs */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12, marginBottom: 18 }}>
          <Kpi label="Orders this month" value={stats.total_orders?.toLocaleString() || '0'} sub={`across ${stats.unique_branches || 0} branches`} />
          <Kpi label="Auto-released" value={stats.auto_released?.toLocaleString() || '0'} sub={`${stats.auto_release_rate || 0}% straight through`} tone="g" />
          <Kpi label="Escalated" value={stats.escalated?.toLocaleString() || '0'} sub="credit review queue" tone="a" />
          <Kpi label="On hold" value={stats.on_hold?.toLocaleString() || '0'} sub="action required" tone="r" />
          <Kpi label="Avg exposure" value={stats.avg_exposure_pct ? `${stats.avg_exposure_pct}%` : 'N/A'} sub="of credit limit" />
        </div>

        {/* Filters */}
        <FilterBar title="Filter Credit Orders" onClear={handleClearFilters}>
          <Field label="Credit decision" htmlFor="decision">
            <Select id="decision" value={filters.decision} onChange={(e) => setFilters((p) => ({ ...p, decision: e.target.value }))}>
              <option value="">All decisions</option>
              <option value="AUTO_RELEASE">Auto-released</option>
              <option value="ESCALATE">Escalated</option>
              <option value="HOLD">On hold</option>
              <option value="CREDIT_APPLICATION">Credit application</option>
            </Select>
          </Field>
          <Field label="Branch" htmlFor="branch">
            <Select id="branch" value={filters.branch} onChange={(e) => setFilters((p) => ({ ...p, branch: e.target.value }))}>
              <option value="">All branches</option>
              {branches.map(b => (
                <option key={b.branch} value={b.branch_name || b.branch}>{b.branch_name || b.branch}</option>
              ))}
            </Select>
          </Field>
          <Field label="Search" htmlFor="search">
            <SearchInput id="search" value={filters.search} onChange={(e) => setFilters((p) => ({ ...p, search: e.target.value }))} placeholder="Customer, PO, or order ID" />
          </Field>
        </FilterBar>

        {/* Table */}
        <table style={{ width: "100%", borderCollapse: "collapse", background: "#fff", borderRadius: 10, overflow: "hidden", border: `1px solid ${COLORS.line}` }}>
          <thead>
            <tr>
              {["Order", "Customer", "Value", "Decision", "Credit Checks", "AI Actions", "Details"].map((h) => (
                <th key={h} style={{ background: COLORS.blue, color: "#fff", fontSize: 14, textTransform: "uppercase", letterSpacing: 0.6, textAlign: "left", padding: "12px 12px", fontWeight: 600 }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} style={{ textAlign: 'center', padding: '40px 20px' }}><Spinner /> Loading credit orders...</td></tr>
            ) : error ? (
              <tr><td colSpan={7} style={{ textAlign: 'center', padding: '40px 20px', color: COLORS.red }}>Error: {error}</td></tr>
            ) : orders.length === 0 ? (
              <tr><td colSpan={7} style={{ textAlign: 'center', padding: '40px 20px', color: COLORS.muted }}><EmptyState title="No orders found" description="No credit orders match the current filters." /></td></tr>
            ) : (
              orders.map((o, i) => (
              <tr key={o.id || o.runId}>
                <td style={tdStyle(i, orders.length)}>
                  <div style={{ fontFamily: "ui-monospace,monospace", fontSize: 16, fontWeight: 600 }}>{o.id || o.runId}</div>
                  <div style={{ color: COLORS.muted, fontSize: 16, marginTop: 3 }}>{o.po}</div>
                </td>
                <td style={tdStyle(i, orders.length)}>
                  {o.customer}
                  <div style={{ color: COLORS.muted, fontSize: 16, marginTop: 3 }}>{o.branch}</div>
                </td>
                <td style={tdStyle(i, orders.length)}>{o.value}</td>
                <td style={tdStyle(i, orders.length)}><Pill tone={o.pill}>{o.decision}</Pill></td>
                <td style={tdStyle(i, orders.length)}>
                  <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                    <span style={{ color: COLORS.green, fontWeight: 600 }}>{o.checksPassed || 0} ✓</span>
                    <span style={{ color: COLORS.red, fontWeight: 600 }}>{o.checksBlocked || 0} ✗</span>
                  </div>
                  <div style={{ color: COLORS.muted, fontSize: 11, marginTop: 2 }}>{o.checksSummary}</div>
                </td>
                <td style={tdStyle(i, orders.length)}>
                  {o.actions && o.actions.length > 0 ? (
                    <div style={{ background: "#f9fafb", border: `1px solid ${COLORS.line}`, borderRadius: 7, padding: "9px 11px", fontSize: 14, lineHeight: 1.65, maxWidth: 400 }}>
                      <div style={!expandedActions[o.id] ? { overflow: "hidden", maxHeight: "42px" } : {}}>
                        {renderActionDetails(o.actions)}
                      </div>
                      {o.actions.length > 1 && (
                        <button type="button" onClick={() => toggleActions(o.id)}
                          style={{ background: "none", border: "none", color: COLORS.blue, fontSize: 14, fontWeight: 600, cursor: "pointer", padding: 0, marginTop: 4 }}>
                          {expandedActions[o.id] ? "Show less" : "Show more"}
                        </button>
                      )}
                    </div>
                  ) : ( <span style={{ color: COLORS.muted }}>-</span> )}
                </td>
                <td style={tdStyle(i, orders.length)}>
                  <IconButton title="View Details" onClick={() => handleViewDetails(o.runId || o.id)}>
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12C2.25 12 5.25 6.75 12 6.75C18.75 6.75 21.75 12 21.75 12C21.75 12 18.75 17.25 12 17.25C5.25 17.25 2.25 12 2.25 12Z"/>
                      <circle cx="12" cy="12" r="3"/>
                    </svg>
                  </IconButton>
                </td>
              </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Scrim */}
      {anyOpen && (
        <div onClick={closeAll} style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,.45)", zIndex: 40 }} />
      )}

      {/* Order drawer / sidebar */}
      <div style={{
        position: "fixed", top: 0, right: 0, bottom: 0, width: "min(660px, 94vw)",
        background: COLORS.page, zIndex: 50,
        transform: drawerOpen ? "translateX(0)" : "translateX(100%)",
        transition: "transform .22s ease", display: "flex", flexDirection: "column",
      }}>
        <div style={{ background: COLORS.blueDark, color: "#fff", padding: "16px 20px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>Credit Decision Review</h3>
          <button onClick={closeAll} style={dcloseStyle}>×</button>
        </div>
        <div style={{ overflowY: "auto", padding: "16px 20px", flex: 1 }}>
          {sidebarLoading ? (
            <div style={{ textAlign: 'center', padding: '40px 20px' }}><Spinner /> Loading details...</div>
          ) : sidebarError ? (
            <div style={{ textAlign: 'center', padding: '40px 20px', color: COLORS.red }}>Error: {sidebarError}</div>
          ) : !selectedOrder ? (
            <div style={{ textAlign: 'center', padding: '40px 20px', color: COLORS.muted }}>No order selected</div>
          ) : (
            <>

              {/* Order Details Card */}
              <Card title="Order details">
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                  <KV k="Order / Run ID" v={selectedOrder.order_id || selectedOrder.run_id} mono />
                  <KV k="Customer PO" v={selectedOrder.customer_po || 'N/A'} mono />
                  <KV k="Customer" v={selectedOrder.customer_name || 'Unknown'} />
                  <KV k="Customer ID" v={selectedOrder.customer_id || 'N/A'} mono />
                  <KV k="Branch" v={selectedOrder.branch_name || selectedOrder.branch || 'N/A'} />
                  <KV k="Order Date" v={selectedOrder.order_date || 'N/A'} />
                  <KV k="Requested Date" v={selectedOrder.requested_date || 'N/A'} />
                  <KV k="Order Value" v={selectedOrder.order_total ? `$${parseFloat(selectedOrder.order_total).toLocaleString('en-US', {minimumFractionDigits: 2})}` : '$0.00'} />
                  <div>
                    <div style={{ fontSize: 10, color: COLORS.muted, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600 }}>SOP</div>
                    <div style={{ marginTop: 2 }}>
                      <SopChip label={`${selectedOrder.sop_id || 'CM-OPS'} · ${selectedOrder.sop_version || 'v1.0'}`} onClick={openSop} />
                    </div>
                  </div>
                </div>
                <div style={{ marginTop: 11, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  <Pill tone={
                    selectedOrder.credit_decision === 'AUTO_RELEASE' ? 'g' :
                    selectedOrder.credit_decision === 'ESCALATE' ? 'a' :
                    selectedOrder.credit_decision === 'HOLD' ? 'r' : 'b'
                  }>
                    {selectedOrder.credit_decision === 'AUTO_RELEASE' ? 'Auto-released' :
                     selectedOrder.credit_decision === 'ESCALATE' ? 'Escalated' :
                     selectedOrder.credit_decision === 'HOLD' ? 'On hold' :
                     selectedOrder.credit_decision || 'Pending'}
                  </Pill>
                  {selectedOrder.credit_evidence?.approval_tier && (
                    <span style={{ background: "#ede9fe", color: "#5b21b6", borderRadius: 5, padding: "3px 8px", fontSize: 10.5, fontWeight: 700 }}>
                      {selectedOrder.credit_evidence.approval_tier} — {
                        selectedOrder.credit_evidence.approval_tier === 'T1' ? 'Supervisor' :
                        selectedOrder.credit_evidence.approval_tier === 'T2' ? 'Credit manager' :
                        selectedOrder.credit_evidence.approval_tier === 'T3' ? 'Controller' :
                        selectedOrder.credit_evidence.approval_tier === 'T4' ? 'CFO' : 'Approver'
                      }
                    </span>
                  )}
                  {selectedOrder.credit_evidence?.sla_hours > 0 && (
                    <span style={{ fontSize: 11, color: COLORS.amber, fontWeight: 600 }}>
                      SLA {selectedOrder.credit_evidence.sla_hours}h
                    </span>
                  )}
                </div>
              </Card>

              {/* Source Document & Line Items */}
              <Card title="Source document & line items">
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.ink }}>{selectedOrder.customer_name?.toUpperCase()}</div>
                    <div style={{ fontSize: 12, color: COLORS.muted, marginTop: 2 }}>PO: {selectedOrder.customer_po || 'N/A'} · {selectedOrder.source_document?.channel || 'PDF'}</div>
                  </div>
                  {(selectedOrder.source_document?.filename || selectedOrder.source_document?.s3_path) && (
                    <button
                      onClick={() => handleOpenSourceDocument(selectedOrder.run_id || selectedOrder.order_id)}
                      style={{ background: 'transparent', border: 'none', color: COLORS.blue, fontSize: 12, fontWeight: 600, cursor: 'pointer', textDecoration: 'underline', display: 'flex', alignItems: 'center', gap: 4 }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" style={{ width: 16, height: 16 }}>
                        <path fillRule="evenodd" d="M4.5 2A1.5 1.5 0 003 3.5v13A1.5 1.5 0 004.5 18h11a1.5 1.5 0 001.5-1.5V7.621a1.5 1.5 0 00-.44-1.06l-4.12-4.122A1.5 1.5 0 0011.378 2H4.5zm2.25 8.5a.75.75 0 000 1.5h6.5a.75.75 0 000-1.5h-6.5zm0 3a.75.75 0 000 1.5h6.5a.75.75 0 000-1.5h-6.5z" clipRule="evenodd" />
                      </svg>
                      View PDF
                    </button>
                  )}
                </div>

                {/* Line items table */}
                {selectedOrder.lines && selectedOrder.lines.length > 0 ? (
                  <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", border: `1px solid ${COLORS.line}`, fontSize: 12 }}>
                      <thead>
                        <tr>
                          {["#", "Item", "Description", "Qty", "Unit Price", "Extended", "Status"].map(h => (
                            <th key={h} style={{ background: "#f8fafc", textAlign: "left", padding: "7px 8px", borderBottom: `1px solid ${COLORS.line}`, fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", color: COLORS.muted }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {selectedOrder.lines.map((line, idx) => {
                          const scenarioBg = line.scenario === 'IM_MATCHED' ? '#eaf7ee' : line.scenario === 'IM_SUPERSEDED' ? '#fef3c7' : '#fff';
                          return (
                            <tr key={idx} style={{ background: scenarioBg }}>
                              <td style={{ padding: "7px 8px", borderBottom: `1px solid ${COLORS.line}` }}>{line.line_no}</td>
                              <td style={{ padding: "7px 8px", borderBottom: `1px solid ${COLORS.line}`, fontFamily: "ui-monospace,monospace", fontSize: 11 }}>
                                {line.item_id}
                                {line.superseded_from && <div style={{ color: '#92400e', fontSize: 10 }}>was: {line.superseded_from}</div>}
                              </td>
                              <td style={{ padding: "7px 8px", borderBottom: `1px solid ${COLORS.line}`, maxWidth: 180 }}>{line.description}</td>
                              <td style={{ padding: "7px 8px", borderBottom: `1px solid ${COLORS.line}`, fontWeight: 600 }}>{line.qty} {line.uom}</td>
                              <td style={{ padding: "7px 8px", borderBottom: `1px solid ${COLORS.line}`, fontFamily: "ui-monospace,monospace" }}>${line.unit_price?.toFixed(2)}</td>
                              <td style={{ padding: "7px 8px", borderBottom: `1px solid ${COLORS.line}`, fontFamily: "ui-monospace,monospace", fontWeight: 600 }}>${line.extended_price?.toLocaleString('en-US', {minimumFractionDigits: 2})}</td>
                              <td style={{ padding: "7px 8px", borderBottom: `1px solid ${COLORS.line}` }}>
                                <Pill tone={line.scenario === 'IM_MATCHED' ? 'g' : line.scenario === 'IM_SUPERSEDED' ? 'a' : 'r'}>
                                  {line.scenario === 'IM_MATCHED' ? 'Matched' : line.scenario === 'IM_SUPERSEDED' ? 'Superseded' : line.status || 'Review'}
                                </Pill>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div style={{ color: COLORS.muted, fontSize: 12 }}>No line items</div>
                )}
              </Card>

              {/* Credit Evidence Card */}
              <Card title="Credit evidence">
                <Row l="Credit limit" r={selectedOrder.credit_evidence?.credit_limit ? `$${parseFloat(selectedOrder.credit_evidence.credit_limit).toLocaleString()}` : '$0'} />
                <Row l="Open AR balance" r={selectedOrder.credit_evidence?.open_ar ? `$${parseFloat(selectedOrder.credit_evidence.open_ar).toLocaleString()}` : '$0'} />
                <Row l="Unshipped exposure" r={selectedOrder.credit_evidence?.unshipped_exposure ? `$${parseFloat(selectedOrder.credit_evidence.unshipped_exposure).toLocaleString()}` : '$0'} />
                <Row l="This order value" r={selectedOrder.credit_evidence?.this_order_value ? `$${parseFloat(selectedOrder.credit_evidence.this_order_value).toLocaleString('en-US', {minimumFractionDigits: 2})}` : '$0'} />
                <Row
                  l="Total if released"
                  r={selectedOrder.credit_evidence?.exposure_total ? `$${parseFloat(selectedOrder.credit_evidence.exposure_total).toLocaleString('en-US', {minimumFractionDigits: 2})} · ${selectedOrder.credit_evidence.exposure_pct}% of limit` : '$0'}
                  rColor={selectedOrder.credit_evidence?.exposure_pct > 150 ? COLORS.red : selectedOrder.credit_evidence?.exposure_pct > 100 ? COLORS.amber : undefined}
                />
                <Row l="Tolerance applied" r={`${selectedOrder.credit_evidence?.tolerance_pct || 0}%`} />
                <Row l="Effective limit" r={selectedOrder.credit_evidence?.effective_limit ? `$${parseFloat(selectedOrder.credit_evidence.effective_limit).toLocaleString()}` : '$0'} />
                <Row l="Avg days to pay" r={selectedOrder.credit_evidence?.avg_days_to_pay ? `${selectedOrder.credit_evidence.avg_days_to_pay} days` : 'N/A'} />
                <Row l="Payment trend" r={selectedOrder.credit_evidence?.days_to_pay_trend || 'N/A'}
                  rColor={selectedOrder.credit_evidence?.days_to_pay_trend === 'WORSENING' ? COLORS.red : selectedOrder.credit_evidence?.days_to_pay_trend === 'IMPROVING' ? COLORS.green : undefined} />
                <Row l="Account tenure" r={selectedOrder.credit_evidence?.account_tenure_months ? `${(selectedOrder.credit_evidence.account_tenure_months / 12).toFixed(1)} years` : 'N/A'} />
                <Row l="Clearing invoice" r={selectedOrder.credit_evidence?.clearing_invoice || 'N/A'} />
              </Card>

              {/* Aging breakdown */}
              {selectedOrder.credit_evidence?.aging && (
                <Card title="AR Aging breakdown">
                  <Row l="Current" r={`$${(selectedOrder.credit_evidence.aging.current || 0).toLocaleString()}`} />
                  <Row l="1–30 days" r={`$${(selectedOrder.credit_evidence.aging.d1_30 || 0).toLocaleString()}`} />
                  <Row l="31–60 days" r={`$${(selectedOrder.credit_evidence.aging.d31_60 || 0).toLocaleString()}`} />
                  <Row l="61–90 days" r={`$${(selectedOrder.credit_evidence.aging.d61_90 || 0).toLocaleString()}`}
                    rColor={(selectedOrder.credit_evidence.aging.d61_90 || 0) > 0 ? COLORS.amber : undefined} />
                  <Row l="90+ days" r={`$${(selectedOrder.credit_evidence.aging.d90_plus || 0).toLocaleString()}`}
                    rColor={(selectedOrder.credit_evidence.aging.d90_plus || 0) > 0 ? COLORS.red : undefined} />
                </Card>
              )}

              {/* Credit Checks Detail Card */}
              {selectedOrder.credit_checks?.details && selectedOrder.credit_checks.details.length > 0 && (
                <Card title={`Credit checks · ${selectedOrder.credit_checks.checks_passed || 0} passed · ${selectedOrder.credit_checks.checks_blocked || 0} blocked`}>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {selectedOrder.credit_checks.details.map((check, idx) => (
                      <div key={idx} style={{
                        background: check.result === 'PASS' ? '#f0fdf4' : check.result === 'BLOCK' ? '#fef2f2' : '#fefce8',
                        border: `1px solid ${check.result === 'PASS' ? '#bbf7d0' : check.result === 'BLOCK' ? '#fecaca' : '#fde68a'}`,
                        borderRadius: 7, padding: "10px 12px",
                      }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.ink }}>
                            <span style={{ fontFamily: "ui-monospace,monospace", marginRight: 6, fontSize: 11, color: COLORS.muted }}>{check.check_id}</span>
                            {check.check_name}
                          </div>
                          <Pill tone={check.result === 'PASS' ? 'g' : check.result === 'BLOCK' ? 'r' : 'a'}>
                            {check.result}
                          </Pill>
                        </div>
                        <div style={{ fontSize: 11.5, color: COLORS.muted, marginTop: 5, lineHeight: 1.5 }}>
                          {check.evidence}
                        </div>
                        <div style={{ display: "flex", gap: 16, marginTop: 6, fontSize: 11 }}>
                          <span><strong style={{ color: COLORS.muted }}>Threshold:</strong> {check.threshold}</span>
                          <span><strong style={{ color: COLORS.muted }}>Actual:</strong> {check.actual_value}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              )}

              {/* Agent Timeline */}
              <Card title="Agent actions performed">
                {selectedOrder.agent_timeline && selectedOrder.agent_timeline.length > 0 ? (
                  selectedOrder.agent_timeline.map((step) => (
                    <Step
                      key={step.step}
                      n={step.step}
                      status={step.status}
                      desc={step.summary || `${step.agent}: ${step.status}`}
                      agent={step.agent}
                      tone={step.status === 'COMPLETED' ? undefined : step.status === 'SKIPPED' ? 's' : 'p'}
                    />
                  ))
                ) : (
                  <div style={{ color: COLORS.muted, fontSize: 13 }}>No agent actions recorded</div>
                )}
              </Card>

              {/* Recommendation */}
              {selectedOrder.credit_evidence?.recommendation && (
                <div style={{ border: "1px solid #f0d9a8", background: COLORS.amberBg, borderRadius: 8, padding: "12px 13px", marginBottom: 14 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: COLORS.amber, letterSpacing: 0.6, marginBottom: 5 }}>
                    AGENT RECOMMENDATION · <SopChip label={`${selectedOrder.sop_id || 'CM-OPS'} · ${selectedOrder.sop_version || 'v1.0'}`} onClick={openSop} />
                  </div>
                  <div style={{ fontSize: 13, lineHeight: 1.6 }}>
                    {selectedOrder.credit_evidence.recommendation}
                  </div>
                  {selectedOrder.credit_evidence?.release_criteria_met && selectedOrder.credit_evidence.release_criteria_met.length > 0 && (
                    <div style={{ marginTop: 10 }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: COLORS.muted, letterSpacing: 0.5, marginBottom: 4 }}>RELEASE CRITERIA</div>
                      {selectedOrder.credit_evidence.release_criteria_met.map((criteria, idx) => (
                        <div key={idx} style={{ fontSize: 12, color: COLORS.ink, marginBottom: 3 }}>• {criteria}</div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Notifications */}
              {selectedOrder.notifications_sent && selectedOrder.notifications_sent.length > 0 && (
                <Card title="Notifications sent">
                  {selectedOrder.notifications_sent.map((notif, idx) => (
                    <div key={idx} style={{ marginBottom: 8, padding: "8px 10px", background: "#f9fafb", borderRadius: 6, border: `1px solid ${COLORS.line}` }}>
                      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                        <Pill tone="b">{notif.channel}</Pill>
                        <span style={{ fontSize: 11, color: COLORS.muted }}>{notif.recipient_role}</span>
                      </div>
                      <div style={{ fontSize: 12, marginTop: 4, color: COLORS.ink }}>{notif.subject}</div>
                    </div>
                  ))}
                </Card>
              )}
            </>
          )}
        </div>

        {/* Footer buttons */}
        {selectedOrder && selectedOrder.credit_decision === 'HOLD' && (
          <div style={{ borderTop: `1px solid ${COLORS.line}`, background: "#fff", padding: "13px 20px", display: "flex", gap: 9, alignItems: "center" }}>
            <button
              onClick={() => handleOrderDecision('REJECTED')}
              style={{...btnStyle, ...((!selectedOrder || sidebarLoading || decisionLoading) && btnDisabledStyle)}}
              disabled={!selectedOrder || sidebarLoading || decisionLoading}
            >
              {decisionLoading ? 'Processing...' : 'Reject order'}
            </button>
            <button
              onClick={() => handleOrderDecision('RELEASED')}
              style={{...btnStyle, ...((!selectedOrder || sidebarLoading || decisionLoading) && btnDisabledStyle)}}
              disabled={!selectedOrder || sidebarLoading || decisionLoading}
            >
              {decisionLoading ? 'Processing...' : 'Release in full'}
            </button>
            <button
              onClick={() => handleOrderDecision('PARTIAL_RELEASE')}
              style={{ ...btnStyle, background: COLORS.blue, borderColor: COLORS.blue, color: "#fff", marginLeft: "auto", ...((!selectedOrder || sidebarLoading || decisionLoading) && btnDisabledStyle) }}
              disabled={!selectedOrder || sidebarLoading || decisionLoading}
            >
              {decisionLoading ? 'Processing...' : 'Approve partial release'}
            </button>
          </div>
        )}

        {/* Message when buttons are not shown */}
        {selectedOrder && selectedOrder.credit_decision !== 'HOLD' && (
          <div style={{ borderTop: `1px solid ${COLORS.line}`, background: "#fff", padding: "13px 20px" }}>
            <p style={{ margin: 0, fontSize: 13, color: COLORS.muted, textAlign: 'center' }}>
              {selectedOrder.credit_decision === 'AUTO_RELEASE' && '✓ This order has been automatically released.'}
              {selectedOrder.credit_decision === 'ESCALATE' && '⚠ This order has been escalated for review.'}
              {selectedOrder.credit_decision === 'CREDIT_APPLICATION' && 'ℹ Credit application is required.'}
              {!['AUTO_RELEASE', 'ESCALATE', 'CREDIT_APPLICATION', 'HOLD'].includes(selectedOrder.credit_decision) && 'No actions available for this order status.'}
            </p>
          </div>
        )}
      </div>

      {/* SOP Panel */}
      <div style={{
        position: "fixed", top: 0, right: 0, bottom: 0, width: "min(760px, 96vw)",
        background: "#fff", zIndex: 60,
        transform: sopKey ? "translateX(0)" : "translateX(100%)",
        transition: "transform .22s ease", display: "flex", flexDirection: "column",
      }}>
        {sopKey && (
          <>
            <div style={{ background: "#312e81", color: "#fff", padding: "14px 20px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div>
                <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>{SOPS[sopKey].title}</h3>
                <div style={{ fontSize: 11.5, color: "#c7d2fe", marginTop: 1 }}>{SOPS[sopKey].sub}</div>
              </div>
              <button onClick={closeSop} style={dcloseStyle}>×</button>
            </div>
            <div style={{ overflowY: "auto", padding: "18px 22px", flex: 1 }}>
              <h5 style={{ fontSize: 15, margin: "0 0 4px" }}>Scenario Dispatch</h5>
              <div style={{ fontSize: 11.5, color: COLORS.muted, marginBottom: 14 }}>
                Used by credit_check_agent · primary branch logic
              </div>
              <SopTable rows={SOPS[sopKey].rows} />
            </div>
            <div style={{ borderTop: `1px solid ${COLORS.line}`, background: "#fff", padding: "13px 20px", display: "flex", gap: 9 }}>
              <button onClick={closeSop} style={btnStyle}>Back to decision</button>
              <button style={{ ...btnStyle, background: COLORS.blue, borderColor: COLORS.blue, color: "#fff", marginLeft: "auto" }}>Edit SOP</button>
            </div>
          </>
        )}
      </div>

      {/* Modals */}
      {modal && modal.type === 'confirm' && (
        <Dialog
          show={true}
          title={modal.title}
          onClose={() => setModal(null)}
          footer={
            <>
              <FioriButton variant="default" onClick={() => setModal(null)}>
                Cancel
              </FioriButton>
              <FioriButton 
                variant="emphasized" 
                onClick={() => {
                  modal.onConfirm();
                  setModal(null);
                }}
              >
                Confirm
              </FioriButton>
            </>
          }
        >
          <div style={{ padding: '16px 20px' }}>
            <p style={{ margin: 0, fontSize: 14, color: COLORS.ink }}>{modal.message}</p>
          </div>
        </Dialog>
      )}

      {modal && modal.type === 'alert' && (
        <Dialog
          show={true}
          title={modal.title}
          onClose={() => {
            setModal(null);
            if (modal.onClose) modal.onClose();
          }}
          footer={
            <FioriButton 
              variant="emphasized" 
              onClick={() => {
                setModal(null);
                if (modal.onClose) modal.onClose();
              }}
            >
              OK
            </FioriButton>
          }
        >
          <div style={{ padding: '16px 20px' }}>
            <p style={{ margin: 0, fontSize: 14, color: COLORS.ink }}>{modal.message}</p>
          </div>
        </Dialog>
      )}

      {modal && modal.type === 'partial_input' && (
        <Dialog
          show={true}
          title={modal.title}
          onClose={() => {
            setModal(null);
            setPartialAmountInput('');
            setNotesInput('');
          }}
          footer={
            <>
              <FioriButton 
                variant="default" 
                onClick={() => {
                  setModal(null);
                  setPartialAmountInput('');
                  setNotesInput('');
                }}
              >
                Cancel
              </FioriButton>
              <FioriButton 
                variant="emphasized" 
                onClick={() => {
                  const amount = parseFloat(partialAmountInput);
                  if (isNaN(amount) || amount <= 0) {
                    setModal({
                      type: 'alert',
                      title: 'Invalid Amount',
                      message: 'Please enter a valid positive amount.',
                    });
                    return;
                  }
                  // Proceed with submission
                  submitDecision(
                    'PARTIAL_RELEASE', 
                    selectedOrder.run_id, 
                    selectedOrder.order_id, 
                    amount, 
                    notesInput
                  );
                }}
              >
                Submit
              </FioriButton>
            </>
          }
        >
          <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <label htmlFor="partial-amount" style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6, color: COLORS.ink }}>
                Partial Release Amount (USD)
              </label>
              <input
                id="partial-amount"
                type="number"
                step="0.01"
                min="0"
                value={partialAmountInput}
                onChange={(e) => setPartialAmountInput(e.target.value)}
                placeholder="Enter amount"
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: `1px solid ${COLORS.line}`,
                  borderRadius: 6,
                  fontSize: 14,
                  color: COLORS.ink,
                }}
              />
            </div>
            <div>
              <label htmlFor="partial-notes" style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6, color: COLORS.ink }}>
                Notes (Optional)
              </label>
              <textarea
                id="partial-notes"
                value={notesInput}
                onChange={(e) => setNotesInput(e.target.value)}
                placeholder="Add any notes or comments"
                rows={3}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: `1px solid ${COLORS.line}`,
                  borderRadius: 6,
                  fontSize: 14,
                  color: COLORS.ink,
                  resize: 'vertical',
                }}
              />
            </div>
          </div>
        </Dialog>
      )}
    </div>
  );
}
